/**
 * Builds a composer request that can send either JSON payloads or FormData.
 */
const buildComposerRequestInit = (data, options = {}) => {
  const isFormData = Object.prototype.toString.call(data) === '[object FormData]';
  const headers = {
    Accept: 'application/json, model/gltf-binary, application/octet-stream'
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  return {
    method: 'post',
    body: isFormData ? data : JSON.stringify(data),
    headers: new Headers(headers),
    signal: options.signal,
  };
};

/**
 * Local composer may return a GLB file directly instead of a JSON wrapper.
 * We normalize both cases to the same `{ s3Url }` contract expected by loaders.
 */
const normalizeComposerResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const blob = await response.blob();
  return {
    s3Url: URL.createObjectURL(blob),
    isLocalComposerBlobUrl: true,
  };
};

const getComposerError = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const errorJson = await response.json();
    return errorJson ? errorJson.message : response.statusText;
  }

  return response.text();
};

/**
 * Sends a request to the local composer service and normalizes the response
 * to the same shape used by stage/prod composer wrappers.
 */
export const postComposerRequest = async (url, data, options = {}) => {
  try {
    const response = await fetch(
      options.domain ? options.domain + url : url,
      buildComposerRequestInit(data, options)
    );

    if (!response.ok) {
      return Promise.reject(await getComposerError(response));
    }

    return normalizeComposerResponse(response);
  } catch (reason) {
    if (reason.name === 'AbortError') {
      console.log('Fetch aborted');
      return;
    }
    return Promise.reject(reason);
  }
};
